import { push, replace } from 'connected-react-router';
import qs from 'query-string';

import type { FLOW } from 'src/app/components/Pages/GetStarted/consts';
import type { ProgressStep, STEP_KEY, STEP_NAME } from 'src/app/components/Pages/GetStarted/StepLayout/consts';
import * as actionTypes from 'src/app/state/actions/types';
import { SET_ONBOARDING_FLOW } from 'src/app/state/actions/types';
import {
  getCurrentPathname,
  getCurrentPathParams,
  getOnboardingCurrentIndex,
  getOnboardingIndexByStepKey,
  getOnboardingSteps,
} from 'src/app/state/selectors';
import type { Dispatch, RootState } from 'src/app/state/store';

/**
 * Inserts a new onboarding step at index
 * @param index number
 * @param step ProgressStep
 */
export function addOnboardingStep(index: number, step: ProgressStep) {
  return (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    //NOTE: prevents addition of steps with duplicate keys
    if (getOnboardingIndexByStepKey(state, step.key) >= 0) return;
    dispatch({
      type: actionTypes.ADD_ONBOARDING_STEP,
      index,
      step,
    });
  };
}
/**
 * Inserts a new onboarding step after given stepKey index
 * @param stepKey STEP_KEY
 * @param step ProgressStep
 */
export function addOnboardingStepAfterGivenStepKey(stepKey: STEP_KEY, step: ProgressStep) {
  return (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    //NOTE: prevents addition of steps with duplicate keys
    if (getOnboardingIndexByStepKey(state, step.key) >= 0) return;
    const insertAtIndex = getOnboardingIndexByStepKey(state, stepKey);
    if (insertAtIndex === -1) return;
    dispatch({
      type: actionTypes.ADD_ONBOARDING_STEP,
      index: insertAtIndex + 1,
      step,
    });
  };
}

export function removeOnboardingStep(stepKey: STEP_KEY) {
  return (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const index = getOnboardingIndexByStepKey(state, stepKey);
    if (index < 0) return;
    dispatch({
      type: actionTypes.REMOVE_ONBOARDING_STEP,
      index,
    });
  };
}

export type EditOnboardStepParams = {
  label?: string;
  stepName?: STEP_NAME;
};

/**
 * Finds a step by STEP_KEY and updates label and or stepName
 * Note: key should not be editable
 *
 * @param stepKey STEP_KEY
 * @param step EditOnboardStepParams
 */
export function editOnboardingStep(stepKey: STEP_KEY, step: EditOnboardStepParams) {
  return (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const index = getOnboardingIndexByStepKey(state, stepKey);
    if (index === -1) return;
    dispatch({
      type: actionTypes.EDIT_ONBOARDING_STEP,
      index,
      step,
    });
  };
}

/**
 * Sets current step layout to the previous steps layout via
 * the previous index number
 */
export function setPreviousOnboardingStep() {
  return (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const onboardingSteps = getOnboardingSteps(state);
    const index = getOnboardingCurrentIndex(state);
    if (index <= 0) return;
    const stepName = onboardingSteps[index - 1].stepName;

    // Note: this ensures we are not overwriting marketing params
    const locationParams = qs.parse(getCurrentPathParams(state));
    locationParams['step'] = stepName.toLowerCase();
    const searchString = qs.stringify(locationParams, 'base');
    const pathName = getCurrentPathname(state);
    // For unclear reasons, react router replace is functionally the same as push in this context.
    dispatch(replace(`${pathName}?${searchString}`));
    dispatch({
      type: actionTypes.SET_CURRENT_ONBOARDING_STEP,
      currentStep: stepName,
    });
  };
}

/**
 * Sets current step layout to the next steps layout via
 * the next index number
 */
export function setNextOnboardingStep() {
  return (dispatch: Dispatch, getState: () => RootState) => {
    const state: RootState = getState();
    const onboardingSteps = getOnboardingSteps(state);
    const index = getOnboardingCurrentIndex(state);
    if (index === -1 || index === onboardingSteps.length - 1) return;
    const stepName = onboardingSteps[index + 1].stepName;

    // Note: this ensures we are not overwriting marketing params
    const locationParams = qs.parse(getCurrentPathParams(state));
    locationParams['step'] = stepName.toLowerCase();
    const searchString = qs.stringify(locationParams, 'base');
    const pathName = getCurrentPathname(state);
    dispatch(push(`${pathName}?${searchString}`));
    dispatch({
      type: actionTypes.SET_CURRENT_ONBOARDING_STEP,
      currentStep: stepName,
    });
  };
}

/**
 * Sets current stepName of type STEP_NAME
 * @param stepName STEP_NAME
 */
export function setCurrentOnboardingStep(stepName: STEP_NAME) {
  return (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();
    const stepFromQueryString = qs.parse(state.router?.location?.search)?.['step']?.toUpperCase();
    if (stepFromQueryString !== stepName) {
      // Note: this ensures we are not overwriting marketing params
      const locationParams = qs.parse(getCurrentPathParams(state));
      const pathName = getCurrentPathname(state);
      locationParams['step'] = stepName.toLowerCase();
      const searchString = qs.stringify(locationParams, 'base');
      dispatch(push(`${pathName}?${searchString}`));
    }
    dispatch({
      type: actionTypes.SET_CURRENT_ONBOARDING_STEP,
      currentStep: stepName,
    });
  };
}

/**
 * Sets current step of onboarding flow but replaces entry in history so back button still works
 * @param stepName STEP_NAME
 */
export function setInitialOnboardingStep(stepName: STEP_NAME) {
  return (dispatch: Dispatch, getState: () => RootState) => {
    const state = getState();

    // Note: this ensures we are not overwriting marketing params
    const locationParams = qs.parse(getCurrentPathParams(state));
    locationParams['step'] = stepName.toLowerCase();
    const pathName = getCurrentPathname(state);
    const searchString = qs.stringify(locationParams, 'base');
    dispatch(replace(`${pathName}?${searchString}`));
    dispatch({
      type: actionTypes.SET_CURRENT_ONBOARDING_STEP,
      currentStep: stepName,
    });
  };
}

/**
 * Sets onboarding form values
 * @param values
 */
export function setOnboardingFormValues(values: { [fieldName: string]: string | boolean | number | null }) {
  return {
    type: actionTypes.SET_ONBOARDING_VALUES,
    values,
  };
}

export function setOnboardingFormError(error: JSX.Element | undefined) {
  return {
    type: actionTypes.SET_ONBOARDING_ERROR,
    error,
  };
}

/**
 * Sets onboarding form values
 * @param values
 */
export function hydrateOnboardingStorage(values: { [key: string]: string }) {
  return {
    type: actionTypes.HYDRATE_ONBOARDING_STORAGE,
    ...values,
  };
}

/**
 * Sets onboarding flow and local storage and fires analytic
 * @param flow FLOW - enum of `DOCTOR_ESCRIPT`, `TRANSFER`, and `NO_RX`
 */
export function setOnboardingFlow(flow: FLOW) {
  return (dispatch: Dispatch) => {
    dispatch({
      type: SET_ONBOARDING_FLOW,
      flow,
    });

    localStorage.setItem('personalizedOnboarding', JSON.stringify({ intent: flow }));
  };
}
